// src/components/EventLayout.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import EventInfo from './EventInfo';
import Spinner from './Spinner';
import ConfirmModal from './ConfirmModal';
import { SERVER_BASE_URL } from "../config";

function EventLayout({ children, hideDescription, hideActions }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    async function fetchEvent() {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${SERVER_BASE_URL}/api/events/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch event');
        const data = await response.json();
        setEvent(data);
      } catch (error) {
        console.error(error);
        setError(error.message);
        toast.error('Could not load event details');
      } finally {
        setLoading(false);
      }
    }
    fetchEvent();
  }, [id]);

  const handleDelete = () => setShowConfirmModal(true);

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${SERVER_BASE_URL}/api/events/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Event deleted successfully!');
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        toast.error(`Failed to delete event: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while deleting the event.');
    } finally {
      setShowConfirmModal(false);
    }
  };

  const handleEdit = async (updatedEvent) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${SERVER_BASE_URL}/api/events/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedEvent),
      });

      if (response.ok) {
        const updated = await response.json();
        setEvent(updated);
        toast.success('Event updated');
      } else {
        toast.error('Failed to update event');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error updating event');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen bg-gray-50"><Spinner /></div>;
  }

  if (error || !event) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">{error ? 'Error Loading Event' : 'Event Not Found'}</h2>
          <p className="text-gray-700 mb-4">{error || 'This event does not exist or has been removed.'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md transition duration-300"
          >Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="container mx-auto max-w-5xl p-4">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <EventInfo
            event={event}
            onEdit={!hideActions ? handleEdit : undefined}
            onDelete={!hideActions ? handleDelete : undefined}
            hideDescription={hideDescription}
            hideActions={hideActions}
          />
          <div className="p-6">{children}</div>
        </div>
      </div>

      {!hideActions && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmDelete}
          message="Are you sure you want to delete this event?"
        />
      )}
    </div>
  );
}

EventLayout.propTypes = {
  children: PropTypes.node,
  hideDescription: PropTypes.bool,
  hideActions: PropTypes.bool,
};

export default EventLayout;
